import { useState, useCallback } from 'react';
import { Search } from 'lucide-react';

interface AddressSearchProps {
  value: string;
  onChange: (address: string) => void;
  required?: boolean;
}

declare global {
  interface Window {
    daum: {
      Postcode: new (options: {
        oncomplete: (data: DaumPostcodeResult) => void;
        width: string;
        height: string;
      }) => { open: () => void };
    };
  }
}

interface DaumPostcodeResult {
  zonecode: string;
  roadAddress: string;
  jibunAddress: string;
  buildingName: string;
}

function loadDaumPostcodeScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.daum?.Postcode) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('주소 검색 스크립트를 불러오지 못했습니다.'));
    document.head.appendChild(script);
  });
}

export default function AddressSearch({ value, onChange, required }: AddressSearchProps) {
  const [detailAddress, setDetailAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const baseAddress = value.split(',')[0]?.trim() || '';
  const savedDetail = value.split(',').slice(1).join(',').trim() || '';

  const handleSearch = useCallback(async () => {
    setIsLoading(true);
    try {
      await loadDaumPostcodeScript();
      new window.daum.Postcode({
        oncomplete: (data: DaumPostcodeResult) => {
          const addr = data.roadAddress || data.jibunAddress;
          const full = `(${data.zonecode}) ${addr}${data.buildingName ? ` ${data.buildingName}` : ''}`;
          setDetailAddress('');
          onChange(full);
        },
        width: '100%',
        height: '100%',
      }).open();
    } catch {
      alert('주소 검색을 불러오는데 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsLoading(false);
    }
  }, [onChange]);

  const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const detail = e.target.value;
    setDetailAddress(detail);
    if (baseAddress) {
      onChange(detail.trim() ? `${baseAddress}, ${detail.trim()}` : baseAddress);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-900 mb-1.5">
        주소 {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={baseAddress}
          readOnly
          placeholder="주소를 검색해주세요"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm cursor-pointer"
          onClick={handleSearch}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={isLoading}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors flex items-center gap-1.5 whitespace-nowrap disabled:opacity-50"
        >
          <Search className="w-4 h-4" />
          {isLoading ? '로딩...' : '주소 검색'}
        </button>
      </div>
      {baseAddress && (
        <input
          type="text"
          value={detailAddress || savedDetail}
          onChange={handleDetailChange}
          placeholder="상세주소를 입력해주세요 (선택)"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary text-sm"
        />
      )}
    </div>
  );
}
